import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { mindboxAuthorizeDevice, mindboxSetCart } from './mindboxEvents';
import { userInfo } from '../store/selectors/user';
import { selectBasketItems } from '../store/selectors/basket';
import { useGetSettings } from '../modules/home/services/getSettings';
import { useGetMindboxUserInfo } from '../modules/home/services/getMindboxUserInfo';

// Клиент, для которого устройство уже авторизовано с момента загрузки
// страницы. В памяти модуля, а не в sessionStorage: тот переживает обновление
// страницы, и после F5 запрос бы не ушёл. Переходы и ремаунты провайдера
// повторного вызова не дают, каждое открытие/обновление веб-аппа — даёт.
let authorizedClientId: string | null = null;

/**
 * Сессия Mindbox: авторизация устройства при открытии веб-аппа и запрос
 * /mb/user-info с deviceUUID. Сами события с фронта в Mindbox не шлём —
 * только через бэкенд.
 *
 * Монтируется один раз — в AppProvider.
 */
export const useMindboxTracking = () => {
  const user = useSelector(userInfo);
  const { isMindboxEnabled } = useGetSettings();
  useGetMindboxUserInfo(isMindboxEnabled && user.isAuth);

  // Открыл веб-апп вошедший клиент — (пере)авторизуем устройство в Mindbox:
  // deviceUUID мог смениться с момента входа.
  useEffect(() => {
    if (!user.isAuth || !user._id) return;
    const clientId = String(user._id);
    if (authorizedClientId === clientId) return;

    // Помечаем до ответа, чтобы параллельный маунт не отправил дубль; при
    // отказе снимаем отметку — следующий маунт попробует снова.
    authorizedClientId = clientId;
    mindboxAuthorizeDevice().then(ok => {
      if (!ok && authorizedClientId === clientId) authorizedClientId = null;
    });
  }, [user.isAuth, user._id]);
};

/**
 * Состав корзины. Отдельным хуком, потому что корзина меняется чаще всего и
 * её достаточно синхронизировать одним «слепком» после того, как изменения
 * улеглись, — вместо события на каждый плюс/минус.
 */
export const useMindboxCartSync = () => {
  const items = useSelector(selectBasketItems);
  const signature = JSON.stringify(
    items
      .filter(item => item.isAvailable)
      .map(item => [item.id, item.quantity, item.total_price])
  );
  const lastSignature = useRef<string | null>(null);

  useEffect(() => {
    // Первый прогон — это гидратация корзины из localStorage, а не действие
    // пользователя: запоминаем состояние и молчим.
    if (lastSignature.current === null) {
      lastSignature.current = signature;
      console.log('[set-cart] первый прогон (гидратация корзины) — не отправляем', items);
      return;
    }

    if (lastSignature.current === signature) return;
    lastSignature.current = signature;

    console.log('[set-cart] корзина изменилась — отправим через 500 мс', items);
    const timer = setTimeout(() => {
      console.log('[set-cart] таймер сработал — вызываем mindboxSetCart');
      mindboxSetCart(items);
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
};
